import React, { useEffect, useMemo, useState } from "react";
import Icon from "@mdi/react";
import {
  mdiAlertOutline,
  mdiClose,
  mdiPlus,
  mdiPencilBoxOutline,
} from "@mdi/js";
import LoadingWidget from "../../../../context_providers/modal/loader";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import WarehouseSearchField from "../../../../erp/inventory/warehouse/warehouse_search_field";
import { WarehouseModel } from "../../../../erp/inventory/models/warehouse_model";
import {
  ItemWarehouseModel,
  UpdateSTPayload,
  WarehouseOutwardModel,
  WarehouseOutwardStatus,
} from "../model/warehouse_outward_model";
import { fetchGet } from "../../../../service/api_client";
import EditableWarehouseItemRow from "../common-components/EditableWarehouseItemRow";
import {
  FetchWarehouseDetailsById,
  GetItemWAC,
  UpdateRejectedeSTApi,
} from "../warehouse_module_repo";
import RejectedDevicesAllocationModal from "./RejectedDevicesAllocationModal";
import "../inward-styles.css";

type EditableItem = ItemWarehouseModel & {
  availableQty?: number;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  selectedOutward?: WarehouseOutwardModel | null;
};

const EditRejectedSTModal: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
  selectedOutward,
}) => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);

  const [fromWarehouse, setFromWarehouse] = useState<WarehouseModel | null>(
    null,
  );
  const [toWarehouse, setToWarehouse] = useState<WarehouseModel | null>(null);
  const [expectedDate, setExpectedDate] = useState<string>("");
  const [purpose, setPurpose] = useState<string>("");
  const [description, setDescription] = useState<string>("");

  const [items, setItems] = useState<EditableItem[]>([]);
  const [deviceAllocationOpen, setDeviceAllocationOpen] = useState(false);
  const [activeDeviceItemIndex, setActiveDeviceItemIndex] = useState<
    number | null
  >(null);

  const isRegularRejected =
    selectedOutward?.type === "ST" &&
    selectedOutward?.st_subtype === "Regular" &&
    selectedOutward?.status === WarehouseOutwardStatus.REJECTED;

  useEffect(() => {
    if (!open || !selectedOutward || !isRegularRejected) return;

    const init = async () => {
      try {
        setInitializing(true);

        let fromWh: WarehouseModel | null = null;

        if (selectedOutward.from_warehouse_id) {
          const fromWhRes = await FetchWarehouseDetailsById(
            selectedOutward.from_warehouse_id,
          );

          if (!fromWhRes?.success || !fromWhRes?.data) {
            showToast({
              type: ToastType.error,
              text: "Failed to load source warehouse",
            });
            return;
          }

          fromWh = fromWhRes.data;
          setFromWarehouse(fromWh);
        }

        if (selectedOutward.to_warehouse_id) {
          const toWhRes = await FetchWarehouseDetailsById(
            selectedOutward.to_warehouse_id,
          );

          if (!toWhRes?.success || !toWhRes?.data) {
            showToast({
              type: ToastType.error,
              text: "Failed to load destination warehouse",
            });
            return;
          }

          setToWarehouse(toWhRes.data);
        }

        setExpectedDate(
          selectedOutward.expected_date
            ? new Date(selectedOutward.expected_date)
                .toISOString()
                .split("T")[0]
            : "",
        );

        setPurpose(selectedOutward.purpose || "");
        setDescription(selectedOutward.description || "");

        const baseItems: EditableItem[] = selectedOutward.items.map((i) => ({
          inventoryItemCode: i.inventoryItemCode,
          itemName: i.itemName,
          requestedQty: i.requestedQty,
          itemPrice: i.itemPrice ?? 0,
          subCategory: i.subCategory,
          itemUnit: i.itemUnit,
          itemHSN: i.itemHSN,
          deviceIds: i.deviceIds ?? [],
          availableQty: undefined,
          gst_applicable: i.gst_applicable,
          tax_detail: i.tax_detail || {
            igst: 0,
            cgst: 0,
            sgst: 0,
          },
        }));

        if (fromWh?.id) {
          const withQty = await Promise.all(
            baseItems.map(async (item) => {
              const availableQty = await fetchAvailableQty(
                fromWh!.id,
                item.inventoryItemCode,
              );
              return {
                ...item,
                availableQty,
              };
            }),
          );
          setItems(withQty);
        } else {
          setItems(baseItems);
        }
      } finally {
        setInitializing(false);
      }
    };

    init();
  }, [open, selectedOutward?.id]);

  const validationError = useMemo(() => {
    if (!fromWarehouse) return "Source warehouse is required";
    if (!toWarehouse) return "Select destination warehouse";
    if (!expectedDate) return "Select expected date";
    if (!purpose.trim()) return "Purpose is required";

    if (fromWarehouse?.id === toWarehouse?.id)
      return "Source and destination cannot be same";

    if (items.length === 0) return "Add at least one item";

    for (const item of items) {
      if (!item.inventoryItemCode) return "Select all items";
      if (item.requestedQty <= 0) return "Quantity must be greater than 0";
      if (
        item.availableQty !== undefined &&
        item.requestedQty > item.availableQty
      ) {
        return "Quantity exceeds available stock";
      }

      // Finished Goods devices are allocated after FG approval, not here — so
      // device allocation is intentionally not required to resubmit.
    }

    return null;
  }, [fromWarehouse, toWarehouse, expectedDate, purpose, items]);

  const totalValue = useMemo(
    () =>
      items.reduce((sum, i) => sum + i.requestedQty * (i.itemPrice ?? 0), 0),
    [items],
  );

  if (!open || !selectedOutward) return null;

  const fetchAvailableQty = async (
    warehouseId: string,
    itemCode: string,
  ): Promise<number> => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/items/warehouses?warehouse_id=${warehouseId}`;
      const res = await fetchGet(url);
      if (!res?.success) return 0;

      const match = res.data.item_data.find(
        (i: any) => i.item_code === itemCode,
      );

      return match?.available_units ?? 0;
    } catch {
      return 0;
    }
  };

  const fetchItemWac = async (
    itemCode: string,
    notify?: (msg: string) => void,
  ): Promise<number> => {
    try {
      const res = await GetItemWAC(itemCode);

      if (res?.success && typeof res?.data?.wac_value === "number") {
        if (res.data.wac_value === 0) {
          notify?.("WAC is not available for this item");
          return 0;
        }
        return res.data.wac_value;
      }

      notify?.("Failed to fetch WAC");
      return 0;
    } catch {
      notify?.("Failed to fetch WAC");
      return 0;
    }
  };

  const addEmptyItem = () => {
    setItems((prev) => [
      ...prev,
      {
        inventoryItemCode: "",
        itemName: "",
        requestedQty: 1,
        itemPrice: 0,
        subCategory: "",
        itemUnit: "",
        itemHSN: "",
        deviceIds: [],
        availableQty: 0,
        tax_detail: {
          igst: 0,
          cgst: 0,
          sgst: 0,
        },
        gst_applicable: false,
      },
    ]);
  };

  const handleItemSelect = async (
    index: number,
    selected: ItemWarehouseModel,
  ) => {
    if (!fromWarehouse) return;

    const exists = items.some(
      (i, idx) =>
        i.inventoryItemCode === selected.inventoryItemCode && idx !== index,
    );

    if (exists) {
      showToast({
        type: ToastType.warning,
        text: "Item already added",
      });
      return;
    }

    const availableQty = await fetchAvailableQty(
      fromWarehouse.id,
      selected.inventoryItemCode,
    );

    if (availableQty === 0) {
      showToast({
        type: ToastType.error,
        text: "Item not available in source warehouse",
      });
      return;
    }

    const wac = await fetchItemWac(selected.inventoryItemCode || "", (msg) =>
      showToast({
        type: ToastType.error,
        text: msg,
      }),
    );

    setItems((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              ...selected,
              requestedQty: 1,
              itemPrice: wac,
              availableQty,
            }
          : item,
      ),
    );
  };

  const updateItem = <K extends keyof EditableItem>(
    index: number,
    field: K,
    value: EditableItem[K],
  ) => {
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    );
  };

  const removeItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const buildUpdatePayload = (): UpdateSTPayload => {
    if (!toWarehouse) {
      throw new Error("Destination warehouse missing");
    }

    return {
      to_warehouse_id: toWarehouse.id,
      to_warehouse_name: toWarehouse.name,
      expected_date: new Date(expectedDate),
      purpose: purpose.trim(),
      description: description.trim(),
      items: items.map((item) => ({
        item_code: item.inventoryItemCode,
        qty: item.requestedQty,
        price: item.itemPrice ?? 0,
        ...(item.deviceIds && item.deviceIds.length > 0
          ? { device_ids: item.deviceIds }
          : {}),
      })),
    };
  };

  const handleSubmit = async () => {
    if (validationError) {
      showToast({ type: ToastType.warning, text: validationError });
      return;
    }

    try {
      setLoading(true);
      const payload = buildUpdatePayload();

      const res = await UpdateRejectedeSTApi(selectedOutward.id, payload);
      if (!res.success) {
        showToast({
          type: ToastType.error,
          text: res.error || "Something went wrong in ST update",
        });
        return;
      }

      showToast({
        type: ToastType.success,
        text: "Regular ST updated successfully",
      });

      onSuccess?.();
      onClose();
    } catch (err: any) {
      showToast({
        type: ToastType.error,
        text: err?.message || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  const openDeviceAllocation = (index: number) => {
    const item = items[index];

    if (!fromWarehouse) {
      showToast({
        type: ToastType.warning,
        text: "Source warehouse is required",
      });
      return;
    }

    if (!item.inventoryItemCode) {
      showToast({
        type: ToastType.warning,
        text: "Please select item first",
      });
      return;
    }

    if (item.requestedQty <= 0) {
      showToast({
        type: ToastType.warning,
        text: "Quantity must be greater than 0",
      });
      return;
    }

    setActiveDeviceItemIndex(index);
    setDeviceAllocationOpen(true);
  };

  const saveDeviceAllocation = (devices: string[]) => {
    if (activeDeviceItemIndex === null) return;

    setItems((prev) =>
      prev.map((item, i) =>
        i === activeDeviceItemIndex ? { ...item, deviceIds: devices } : item,
      ),
    );

    setDeviceAllocationOpen(false);
    setActiveDeviceItemIndex(null);
  };

  if (!isRegularRejected) {
    return (
      <div
        id="warehouse-outward-edit-rejected-st-not-allowed-modal"
        className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center p-4"
      >
        <div className="w-full max-w-xl bg-white rounded-lg flex flex-col wh-modal-xs max-h-[90vh] overflow-y-auto">
          <div className="border-b px-4 py-3 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Icon path={mdiAlertOutline} size={1} className="text-red-600" />
              <h2 className="font-semibold text-lg">Edit Not Allowed</h2>
            </div>
            <button
              id="warehouse-outward-edit-rejected-st-not-allowed-close-icon-btn"
              onClick={onClose}
            >
              <Icon path={mdiClose} size={0.9} />
            </button>
          </div>

          <div className="p-4 text-sm text-gray-700">
            Only <span className="font-semibold">Regular ST</span> in
            <span className="font-semibold"> Rejected</span> status can be
            edited. Returnable and Replacement ST are cancelled directly after
            finance rejection.
          </div>

          <div className="border-t px-4 py-3 flex justify-end">
            <button
              id="warehouse-outward-edit-rejected-st-not-allowed-close-btn"
              onClick={onClose}
              className="border px-4 py-2 rounded-md"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      id="warehouse-outward-edit-rejected-st-modal"
      className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center p-4"
    >
      <div className="w-full max-w-3xl bg-white rounded-lg flex flex-col max-h-[90vh] wh-modal-xs">
        <div className="border-b px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Icon path={mdiPencilBoxOutline} size={1} />
            <h2 className="font-semibold text-lg">Edit Rejected Regular ST</h2>
          </div>
          <button
            id="warehouse-outward-edit-rejected-st-close-icon-btn"
            onClick={onClose}
          >
            <Icon path={mdiClose} size={0.9} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          <div className="flex gap-2 border rounded-md p-3 text-sm warning-card-yellow ">
            <Icon path={mdiAlertOutline} size={1} className="text-amber-600" />
            <div>
              <p className="font-semibold">Finance Rejection Reason</p>
              <p>{selectedOutward.rejection_reason}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">From Warehouse *</label>
              <input
                id="warehouse-outward-edit-rejected-st-from-warehouse-input"
                value={fromWarehouse?.name || ""}
                disabled
                className="h-10 w-full border rounded px-3 bg-gray-100 text-gray-600"
                placeholder="Source warehouse"
              />
            </div>

            <div>
              <label className="text-sm font-medium">To Warehouse *</label>
              <WarehouseSearchField
                id="warehouse-outward-edit-rejected-st-to-warehouse-search"
                disabled={!fromWarehouse}
                value={toWarehouse || undefined}
                onSelect={setToWarehouse}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Expected Date *</label>
              <input
                id="warehouse-outward-edit-rejected-st-expected-date-input"
                type="date"
                value={expectedDate}
                onChange={(e) => setExpectedDate(e.target.value)}
                className="h-10 w-full border rounded px-3"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Purpose *</label>
              <input
                id="warehouse-outward-edit-rejected-st-purpose-input"
                type="text"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                className="h-10 w-full border rounded px-3"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Description</label>
            <textarea
              id="warehouse-outward-edit-rejected-st-description-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full border rounded px-3 py-2"
            />
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-base">Items</h3>
              <button
                id="warehouse-outward-edit-rejected-st-add-item-btn"
                onClick={addEmptyItem}
                className="h-9 px-3 border rounded flex items-center gap-1"
              >
                <Icon path={mdiPlus} size={0.8} />
                Add Item
              </button>
            </div>

            {items.some((i) => i.subCategory === "Finished Goods") && (
              <div className="flex gap-2 border rounded-md p-3 text-sm warning-card-yellow">
                <Icon
                  path={mdiAlertOutline}
                  size={0.9}
                  className="text-amber-600"
                />
                <p>
                  Finished Goods devices don't need to be allocated here — after
                  resubmitting, this transfer goes back to FG approval and
                  devices are allocated in the device-allocation step.
                </p>
              </div>
            )}

            {items.map((item, index) => (
              <EditableWarehouseItemRow
                key={index}
                item={item}
                index={index}
                disabledItemSearch={!fromWarehouse}
                onSelectItem={handleItemSelect}
                onChangeQty={(i, qty) => updateItem(i, "requestedQty", qty)}
                onRemove={removeItem}
                onEditDevices={
                  item.subCategory === "Finished Goods"
                    ? () => openDeviceAllocation(index)
                    : undefined
                }
              />
            ))}
          </div>

          <div className="flex justify-between items-center p-4 bg-muted rounded">
            <div>
              <span className="font-medium">Total Value</span>
              <p className="text-xs text-muted-foreground">
                Based on entered prices
              </p>
            </div>
            <span className="text-xl font-bold">
              {"\u20B9"}
              {totalValue}
            </span>
          </div>
        </div>

        <div className="border-t px-4 py-3 flex justify-end gap-3">
          <button
            id="warehouse-outward-edit-rejected-st-cancel-btn"
            onClick={onClose}
            className="border px-4 py-2 rounded-md"
          >
            Cancel
          </button>
          <button
            id="warehouse-outward-edit-rejected-st-resubmit-btn"
            onClick={handleSubmit}
            disabled={!!validationError || loading || initializing}
            className={`bg-blue-700 text-white px-4 py-2 rounded-md transition-all flex items-center justify-center min-w-[160px] ${
              !!validationError || loading || initializing
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-blue-800"
            }`}
          >
            {loading ? "Resubmitting..." : "Resubmit Regular ST"}
          </button>
        </div>

        {(loading || initializing) && <LoadingWidget />}

        {activeDeviceItemIndex !== null && (
          <RejectedDevicesAllocationModal
            open={deviceAllocationOpen}
            from_warehouse_id={fromWarehouse?.id || ""}
            selectedItem={items[activeDeviceItemIndex]}
            onClose={() => {
              setDeviceAllocationOpen(false);
              setActiveDeviceItemIndex(null);
            }}
            onSave={saveDeviceAllocation}
          />
        )}
      </div>
    </div>
  );
};

export default EditRejectedSTModal;
